import React, { useState, useEffect, useRef } from 'react';
import { Box } from '@mui/material';
import { useParams } from 'react-router-dom';
import { useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import ChatSidebar from './ChatSidebar';
import ChatHeader from './ChatHeader';
import ChatMessages from './ChatMessages';
import ChatInput from './ChatInput';
import { SLATE } from '../../constants/Colors';
import useTwilioChat from '../../hooks/useTwilioChat';
import useApiCall from '../../hooks/useApiCall';
import LoadingSpinner from '../../components/LoadingSpinner';

export default function Chat() {
  const { agent_id: agentId } = useParams();
  const currentApp = useSelector(state => state?.apps?.selectedApp);
  const selectedAgentSite = useSelector(state => state?.apps?.selectedAgentSiteCode);
  const tokens = useSelector(state => state?.tokens.value);
  const { makeRequest } = useApiCall();

  const navigate = useNavigate();

  const [sidebarExpanded, setSidebarExpanded] = useState(true);
  const [activeStep] = useState(0);
  const [conversationSid, setConversationSid] = useState(null);
  const [conversationLoading, setConversationLoading] = useState(true);
  const [participants, setParticipants] = useState({});

  // Track authors we've already attempted to resolve — prevents repeated calls
  // when a participant simply isn't returned by the server
  const resolvedAuthorsRef = useRef(new Set());

  const appName = currentApp?.url_code || currentApp?.name || '';

  useEffect(() => {
    if (!agentId || !currentApp?.id) return;

    let cancelled = false;

    const resolveConversation = async () => {
      setConversationLoading(true);
      try {
        // Check if conversation already exists
        const getResponse = await makeRequest(
          `/v1/apps/${currentApp?.id}/agents/${agentId}/conversation`,
          {
            method: 'GET',
            authTokens: tokens,
            ignore_error: true,
          }
        );
        if (!cancelled && getResponse?.agent_conversation?.conversation_id) {
          setConversationSid(getResponse.agent_conversation.conversation_id);
          setParticipants(getResponse.participants || {});
          return;
        }
      } catch (_err) {
        // Conversation not found — fall through to create
        console.log('No existing conversation, will create a new one.', _err);
      } finally {
        if (!cancelled) setConversationLoading(false);
      }

      try {
        // Create the conversation
        const postResponse = await makeRequest(
          `/v1/apps/${currentApp?.id}/agents/${agentId}/conversation`,
          {
            method: 'POST',
            authTokens: tokens,
          }
        );
        if (!cancelled && postResponse?.agent_conversation?.conversation_id) {
          setConversationSid(postResponse.agent_conversation.conversation_id);
          setParticipants(postResponse.participants || {});
        }
      } catch (_err) {
        console.log('Failed to create conversation:', _err);
      } finally {
        if (!cancelled) setConversationLoading(false);
      }
    };

    resolveConversation();

    return () => {
      cancelled = true;
    };
  }, [agentId, currentApp?.id]); // eslint-disable-line react-hooks/exhaustive-deps

  const {
    messages: twilioMessages,
    sendMessage,
    sendMessageWithMedia,
    connectionState,
    isLoading: chatLoading,
    hasMoreHistory,
    isLoadingMore,
    loadMoreHistory,
  } = useTwilioChat(conversationSid);

  // When a message arrives from an author not in participants (user added on the fly),
  // refresh the participants list once per unknown author.
  useEffect(() => {
    if (!twilioMessages.length || !conversationSid) return;

    const unknownAuthors = twilioMessages
      .map(m => m.author)
      .filter(a => a && !participants[a] && !resolvedAuthorsRef.current.has(a));

    if (unknownAuthors.length === 0) return;

    // Mark them before the call so a re-render mid-flight doesn't trigger duplicates
    unknownAuthors.forEach(a => resolvedAuthorsRef.current.add(a));

    makeRequest(`/v1/apps/${currentApp?.id}/agents/${agentId}/conversation`, {
      method: 'GET',
      authTokens: tokens,
    })
      .then(data => {
        if (data?.participants) {
          setParticipants(prev => ({ ...prev, ...data.participants }));
        }
      })
      .catch(() => {});
  }, [twilioMessages, participants, conversationSid]); // eslint-disable-line react-hooks/exhaustive-deps

  const handleSend = (text, files = []) => {
    if (files.length > 0) {
      sendMessageWithMedia(text, files);
    } else {
      sendMessage(text);
    }
  };

  const handleBuildManually = () => {
    navigate(`/${appName}/agent/${agentId}/profile`);
  };

  if (conversationLoading) {
    return <LoadingSpinner message="Setting up your chat..." />;
  }

  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        height: '100vh',
        bgcolor: SLATE[50],
        overflow: 'hidden',
      }}
    >
      {/* Top header bar */}
      <ChatHeader
        appName={appName}
        agentName={selectedAgentSite}
        agentId={agentId}
        onBuildManually={handleBuildManually}
      />

      {/* Body: sidebar + chat area */}
      <Box sx={{ display: 'flex', flex: 1, overflow: 'hidden' }}>
        <ChatSidebar
          expanded={sidebarExpanded}
          onToggle={() => setSidebarExpanded(prev => !prev)}
          activeStepIndex={activeStep}
        />

        {/* Main chat panel */}
        <Box
          sx={{
            flex: 1,
            display: 'flex',
            flexDirection: 'column',
            overflow: 'hidden',
            mx: 3,
            my: 2,
            bgcolor: '#fff',
            borderRadius: '16px',
            border: `1px solid ${SLATE[200]}`,
            boxShadow: '0 1px 4px rgba(0,0,0,0.06)',
          }}
        >
          {chatLoading ? (
            <Box sx={{ flex: 1, display: 'flex', overflow: 'hidden' }}>
              <LoadingSpinner message="Loading conversation..." subMessage="" />
            </Box>
          ) : (
            <ChatMessages
              messages={twilioMessages}
              participants={participants}
              hasMore={hasMoreHistory}
              isLoadingMore={isLoadingMore}
              onLoadMore={loadMoreHistory}
            />
          )}
          <ChatInput
            onSend={handleSend}
            disabled={connectionState !== 'connected' || chatLoading}
          />
        </Box>
      </Box>
    </Box>
  );
}
